import type {
  AnalyticsFilter,
  Deployment,
  Installation,
  Platform,
  RevenueEntry,
  Seed,
  SeedEventRow,
  Subscription,
} from "@seed-factory/core";
import { demoData } from "../fixtures/demo";
import { appConfig } from "./config";
import type {
  DecisionRow,
  EstufaData,
  ExperimentRow,
  IncidentRow,
  LessonRow,
  MetricSnapshotRow,
  OpportunityScoreRow,
  ReleaseRow,
  ResearchAppRow,
  ReviewRow,
  SupportTicketRow,
} from "./local-types";
import { supabase } from "./supabase";

function mapPlatform(row: Record<string, unknown>): Platform {
  return {
    id: String(row.platform_id),
    slug: row.slug as Platform["slug"],
    name: String(row.name),
  };
}
function mapSeed(row: Record<string, unknown>): Seed {
  return {
    id: String(row.seed_id),
    seedKey: String(row.seed_key),
    name: String(row.name),
    summary: row.summary ? String(row.summary) : null,
    status: row.status as Seed["status"],
    createdAt: String(row.created_at),
  };
}
function mapDeployment(row: Record<string, unknown>, platforms: Platform[]): Deployment {
  const platform = platforms.find((item) => item.id === String(row.platform_id));
  return {
    id: String(row.deployment_id),
    seedId: String(row.seed_id),
    platformId: String(row.platform_id),
    platformSlug: platform?.slug ?? "custom",
    name: String(row.name),
    externalAppId: row.external_app_id ? String(row.external_app_id) : null,
    status: String(row.status),
    isPrimary: Boolean(row.is_primary),
  };
}
function mapInstallation(row: Record<string, unknown>): Installation {
  return {
    id: String(row.installation_id),
    deploymentId: String(row.deployment_id),
    tenantId: String(row.tenant_id),
    status: String(row.status),
    installedAt: String(row.installed_at),
    activatedAt: row.activated_at ? String(row.activated_at) : null,
    onboardingCompletedAt: row.onboarding_completed_at ? String(row.onboarding_completed_at) : null,
    firstValueAt: row.first_value_at ? String(row.first_value_at) : null,
    trialStartedAt: row.trial_started_at ? String(row.trial_started_at) : null,
    paidStartedAt: row.paid_started_at ? String(row.paid_started_at) : null,
    uninstalledAt: row.uninstalled_at ? String(row.uninstalled_at) : null,
  };
}
function mapSubscription(row: Record<string, unknown>): Subscription {
  return {
    id: String(row.subscription_id),
    deploymentId: String(row.deployment_id),
    tenantId: String(row.tenant_id),
    status: String(row.status),
    planExternalId: row.plan_external_id ? String(row.plan_external_id) : null,
    mrrCents: Number(row.mrr_cents ?? 0),
    currency: String(row.currency ?? "BRL"),
    startedAt: String(row.started_at),
    trialStartedAt: row.trial_started_at ? String(row.trial_started_at) : null,
    trialEndsAt: row.trial_ends_at ? String(row.trial_ends_at) : null,
    canceledAt: row.canceled_at ? String(row.canceled_at) : null,
  };
}
function mapRevenue(row: Record<string, unknown>): RevenueEntry {
  return {
    id: String(row.revenue_entry_id),
    deploymentId: String(row.deployment_id),
    tenantId: row.tenant_id ? String(row.tenant_id) : null,
    amountCents: Number(row.amount_cents ?? 0),
    currency: String(row.currency ?? "BRL"),
    kind: String(row.kind),
    recognizedAt: String(row.recognized_at),
  };
}
function mapEvent(row: Record<string, unknown>, deployments: Deployment[]): SeedEventRow {
  const deployment = deployments.find((item) => item.id === String(row.deployment_id));
  return {
    id: String(row.event_row_id),
    schemaVersion: 1,
    seedId: String(row.seed_id),
    deploymentId: String(row.deployment_id),
    platform: deployment?.platformSlug ?? "custom",
    tenantId: String(row.tenant_id),
    eventId: String(row.event_id),
    eventName: String(row.event_name),
    eventFamily: row.event_family as SeedEventRow["eventFamily"],
    occurredAt: String(row.occurred_at),
    value: row.value === null || row.value === undefined ? undefined : Number(row.value),
    metadata: (row.metadata ?? {}) as SeedEventRow["metadata"],
    source: row.source as SeedEventRow["source"],
    ingestedAt: String(row.ingested_at),
  };
}

async function selectAll(table: string, columns = "*") {
  if (!supabase) throw new Error("SUPABASE_NOT_CONFIGURED");
  const { data, error } = await supabase.from(table).select(columns);
  if (error) throw new Error(`${table.toUpperCase()}_LOAD_FAILED`);
  return (data ?? []) as unknown as Record<string, unknown>[];
}

const PAGE_SIZE = 1000;

type PageResult = { data: unknown; error: unknown };

async function collectPages(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult>,
): Promise<Record<string, unknown>[]> {
  const rows: Record<string, unknown>[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const result = await fetchPage(from, from + PAGE_SIZE - 1);
    if (result.error) throw new Error("ESTUFA_DATA_LOAD_FAILED");
    const page = Array.isArray(result.data)
      ? (result.data as unknown as Record<string, unknown>[])
      : [];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

export async function loadEstufaData(filter: AnalyticsFilter): Promise<EstufaData> {
  if (appConfig.demoMode) return demoData;
  if (!supabase) throw new Error("SUPABASE_NOT_CONFIGURED");

  const client = supabase;

  const [platformRows, seedRows, deploymentRows] = await Promise.all([
    selectAll("platforms"),
    selectAll("seeds"),
    selectAll("seed_deployments"),
  ]);
  const platforms = platformRows.map(mapPlatform);
  const seeds = seedRows.map(mapSeed);
  const deployments = deploymentRows.map((row) => mapDeployment(row, platforms));
  const allowedDeployments = deployments.filter((deployment) => {
    if (filter.seedId && deployment.seedId !== filter.seedId) return false;
    if (filter.deploymentId && deployment.id !== filter.deploymentId) return false;
    if (filter.platformId && deployment.platformId !== filter.platformId) return false;
    return true;
  });
  const ids = allowedDeployments.map((item) => item.id);
  if (ids.length === 0) {
    return {
      platforms,
      seeds,
      deployments,
      installations: [],
      events: [],
      subscriptions: [],
      revenue: [],
      metricSnapshots: [],
      releases: [],
      decisions: [],
      lessons: [],
      incidents: [],
      experiments: [],
      supportTickets: [],
      reviews: [],
      opportunityScores: [],
      researchApps: [],
    };
  }

  const fromIso = filter.from.toISOString();
  const toIso = filter.to.toISOString();
  const [
    installationRows,
    subscriptionRows,
    eventRows,
    revenueRows,
    metricRows,
    releaseRows,
    decisionRows,
    lessonRows,
    incidentRows,
    experimentRows,
    supportRows,
    reviewRows,
    scoreRows,
    researchRows,
  ] = await Promise.all([
    collectPages((from, to) =>
      client
        .from("installations")
        .select("*")
        .in("deployment_id", ids)
        .lte("installed_at", toIso)
        .order("installation_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("subscriptions")
        .select("*")
        .in("deployment_id", ids)
        .lte("started_at", toIso)
        .order("subscription_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("seed_events")
        .select("*")
        .in("deployment_id", ids)
        .gte("occurred_at", fromIso)
        .lte("occurred_at", toIso)
        .order("occurred_at", { ascending: true })
        .order("event_row_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("revenue_entries")
        .select("*")
        .in("deployment_id", ids)
        .gte("recognized_at", fromIso)
        .lte("recognized_at", toIso)
        .order("recognized_at", { ascending: true })
        .order("revenue_entry_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("metric_snapshots")
        .select("*")
        .in("deployment_id", ids)
        .lte("period_start", toIso)
        .gte("period_end", fromIso)
        .order("period_start", { ascending: true })
        .order("metric_snapshot_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("releases")
        .select("*")
        .in("deployment_id", ids)
        .order("released_at", { ascending: false })
        .order("release_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) => {
      let query = client.from("decisions").select("*");
      if (filter.seedId) query = query.or(`seed_id.is.null,seed_id.eq.${filter.seedId}`);
      return query
        .order("decided_at", { ascending: false })
        .order("decision_id", { ascending: true })
        .range(from, to);
    }),
    collectPages((from, to) => {
      let query = client.from("lessons").select("*");
      if (filter.seedId)
        query = query.or(`source_seed_id.is.null,source_seed_id.eq.${filter.seedId}`);
      return query
        .order("created_at", { ascending: false })
        .order("lesson_id", { ascending: true })
        .range(from, to);
    }),
    collectPages((from, to) =>
      client
        .from("incidents")
        .select("*")
        .in("deployment_id", ids)
        .order("opened_at", { ascending: false })
        .order("incident_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("experiments")
        .select("*")
        .or(`deployment_id.is.null,deployment_id.in.(${ids.join(",")})`)
        .order("created_at", { ascending: false })
        .order("experiment_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("support_tickets")
        .select("*")
        .or(`deployment_id.is.null,deployment_id.in.(${ids.join(",")})`)
        .order("opened_at", { ascending: false })
        .order("support_ticket_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("reviews")
        .select("*")
        .or(`deployment_id.is.null,deployment_id.in.(${ids.join(",")})`)
        .order("reviewed_at", { ascending: false })
        .order("review_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("opportunity_scores")
        .select("*")
        .order("scored_at", { ascending: false })
        .order("opportunity_score_id", { ascending: true })
        .range(from, to),
    ),
    collectPages((from, to) =>
      client
        .from("research_apps")
        .select("*")
        .order("observed_at", { ascending: false })
        .order("research_app_id", { ascending: true })
        .range(from, to),
    ),
  ]);

  const metricSnapshots: MetricSnapshotRow[] = metricRows.map((row) => ({
    id: String(row.metric_snapshot_id),
    deploymentId: String(row.deployment_id),
    metricName: String(row.metric_name),
    value: Number(row.value),
    periodStart: String(row.period_start),
    periodEnd: String(row.period_end),
    granularity: row.granularity as MetricSnapshotRow["granularity"],
    source: row.source as MetricSnapshotRow["source"],
    dimensions: (row.dimensions ?? {}) as Record<string, unknown>,
    observedAt: String(row.observed_at),
  }));
  const releases: ReleaseRow[] = releaseRows.map((row) => ({
    id: String(row.release_id),
    deploymentId: String(row.deployment_id),
    version: String(row.version),
    commitSha: row.commit_sha ? String(row.commit_sha) : null,
    summary: String(row.summary),
    hypothesis: row.hypothesis ? String(row.hypothesis) : null,
    riskLevel: row.risk_level as ReleaseRow["riskLevel"],
    status: String(row.status),
    releasedAt: String(row.released_at),
  }));
  const decisions: DecisionRow[] = decisionRows.map((row) => ({
    id: String(row.decision_id),
    seedId: row.seed_id ? String(row.seed_id) : null,
    deploymentId: row.deployment_id ? String(row.deployment_id) : null,
    title: String(row.title),
    category: String(row.category),
    hypothesis: row.hypothesis ? String(row.hypothesis) : null,
    changeSummary: String(row.change_summary),
    rationale: String(row.rationale),
    targetMetric: row.target_metric ? String(row.target_metric) : null,
    observedResult: row.observed_result ? String(row.observed_result) : null,
    verdict: (row.verdict ?? null) as DecisionRow["verdict"],
    confidence: row.confidence === null ? null : Number(row.confidence),
    decidedAt: String(row.decided_at),
  }));
  const lessons: LessonRow[] = lessonRows.map((row) => ({
    id: String(row.lesson_id),
    sourceSeedId: row.source_seed_id ? String(row.source_seed_id) : null,
    category: String(row.category),
    evidenceType: row.evidence_type as LessonRow["evidenceType"],
    confidence: row.confidence === null ? null : Number(row.confidence),
    lesson: String(row.lesson),
    whatHappened: String(row.what_happened),
    whyWeThinkItHappened: row.why_we_think_it_happened
      ? String(row.why_we_think_it_happened)
      : null,
    whatToRepeat: row.what_to_repeat ? String(row.what_to_repeat) : null,
    whatNotToRepeat: row.what_not_to_repeat ? String(row.what_not_to_repeat) : null,
    appliesTo: row.applies_to ? String(row.applies_to) : null,
    createdAt: String(row.created_at),
  }));
  const incidents: IncidentRow[] = incidentRows.map((row) => ({
    id: String(row.incident_id),
    deploymentId: row.deployment_id ? String(row.deployment_id) : null,
    title: String(row.title),
    severity: row.severity as IncidentRow["severity"],
    status: row.status as IncidentRow["status"],
    openedAt: String(row.opened_at),
    resolvedAt: row.resolved_at ? String(row.resolved_at) : null,
    releaseVersion: row.release_version ? String(row.release_version) : null,
  }));
  const experiments: ExperimentRow[] = experimentRows.map((row) => ({
    id: String(row.experiment_id),
    deploymentId: row.deployment_id ? String(row.deployment_id) : null,
    name: String(row.name),
    hypothesis: String(row.hypothesis),
    primaryMetric: String(row.primary_metric),
    startsAt: row.starts_at ? String(row.starts_at) : null,
    endsAt: row.ends_at ? String(row.ends_at) : null,
    verdict: (row.verdict ?? null) as ExperimentRow["verdict"],
  }));
  const supportTickets: SupportTicketRow[] = supportRows.map((row) => ({
    id: String(row.support_ticket_id),
    deploymentId: row.deployment_id ? String(row.deployment_id) : null,
    tenantRef: row.tenant_ref ? String(row.tenant_ref) : null,
    category: row.category ? String(row.category) : null,
    severity: (row.severity ?? null) as SupportTicketRow["severity"],
    reason: row.reason ? String(row.reason) : null,
    openedAt: String(row.opened_at),
    resolvedAt: row.resolved_at ? String(row.resolved_at) : null,
    firstResponseMinutes:
      row.first_response_minutes === null ? null : Number(row.first_response_minutes),
    resolutionMinutes: row.resolution_minutes === null ? null : Number(row.resolution_minutes),
    releaseVersion: row.release_version ? String(row.release_version) : null,
  }));
  const reviews: ReviewRow[] = reviewRows.map((row) => ({
    id: String(row.review_id),
    deploymentId: row.deployment_id ? String(row.deployment_id) : null,
    rating: row.rating === null ? null : Number(row.rating),
    reviewedAt: String(row.reviewed_at),
    publicReference: row.public_reference ? String(row.public_reference) : null,
    categoryTags: Array.isArray(row.category_tags) ? row.category_tags.map(String) : [],
  }));
  const opportunityScores: OpportunityScoreRow[] = scoreRows.map((row) => ({
    id: String(row.opportunity_score_id),
    candidateKey: String(row.candidate_key),
    candidateName: String(row.candidate_name),
    productScore: row.product_score === null ? null : Number(row.product_score),
    distributionScore: row.distribution_score === null ? null : Number(row.distribution_score),
    platformRiskScore: row.platform_risk_score === null ? null : Number(row.platform_risk_score),
    buildScore: row.build_score === null ? null : Number(row.build_score),
    status: String(row.status),
    evidence: row.evidence ? String(row.evidence) : null,
    scoredAt: String(row.scored_at),
  }));
  const researchApps: ResearchAppRow[] = researchRows.map((row) => ({
    id: String(row.research_app_id),
    platformId: row.platform_id ? String(row.platform_id) : null,
    developer: row.developer ? String(row.developer) : null,
    appName: String(row.app_name),
    category: row.category ? String(row.category) : null,
    reviewsCount: row.reviews_count === null ? null : Number(row.reviews_count),
    rating: row.rating === null ? null : Number(row.rating),
    priceLowestPaid: row.price_lowest_paid === null ? null : Number(row.price_lowest_paid),
    trialDays: row.trial_days === null ? null : Number(row.trial_days),
    pain: row.pain ? String(row.pain) : null,
    promisedOutcome: row.promised_outcome ? String(row.promised_outcome) : null,
    observedAt: String(row.observed_at),
  }));

  return {
    platforms,
    seeds,
    deployments,
    installations: installationRows.map(mapInstallation),
    subscriptions: subscriptionRows.map(mapSubscription),
    events: eventRows.map((row) => mapEvent(row, deployments)),
    revenue: revenueRows.map(mapRevenue),
    metricSnapshots,
    releases,
    decisions,
    lessons,
    incidents,
    experiments,
    supportTickets,
    reviews,
    opportunityScores,
    researchApps,
  };
}

export async function createManualMetric(input: {
  deploymentId: string;
  metricName: string;
  value: number;
  periodStart: string;
  periodEnd: string;
  granularity: "day" | "week" | "month" | "snapshot";
}) {
  if (!supabase || appConfig.demoMode) throw new Error("WRITE_REQUIRES_REAL_SUPABASE");
  const { error } = await supabase.from("metric_snapshots").insert({
    deployment_id: input.deploymentId,
    metric_name: input.metricName,
    value: input.value,
    period_start: input.periodStart,
    period_end: input.periodEnd,
    granularity: input.granularity,
    source: "manual_entry",
  });
  if (error) throw new Error("METRIC_SNAPSHOT_CREATE_FAILED");
}

export async function createDecision(
  input: Omit<DecisionRow, "id" | "decidedAt" | "observedResult" | "verdict" | "confidence">,
) {
  if (!supabase || appConfig.demoMode) throw new Error("WRITE_REQUIRES_REAL_SUPABASE");
  const { error } = await supabase.from("decisions").insert({
    seed_id: input.seedId,
    deployment_id: input.deploymentId,
    title: input.title,
    category: input.category,
    hypothesis: input.hypothesis,
    change_summary: input.changeSummary,
    rationale: input.rationale,
    target_metric: input.targetMetric,
  });
  if (error) throw new Error("DECISION_CREATE_FAILED");
}

export async function createLesson(input: Omit<LessonRow, "id" | "createdAt">) {
  if (!supabase || appConfig.demoMode) throw new Error("WRITE_REQUIRES_REAL_SUPABASE");
  const { error } = await supabase.from("lessons").insert({
    source_seed_id: input.sourceSeedId,
    category: input.category,
    evidence_type: input.evidenceType,
    confidence: input.confidence,
    lesson: input.lesson,
    what_happened: input.whatHappened,
    why_we_think_it_happened: input.whyWeThinkItHappened,
    what_to_repeat: input.whatToRepeat,
    what_not_to_repeat: input.whatNotToRepeat,
    applies_to: input.appliesTo,
  });
  if (error) throw new Error("LESSON_CREATE_FAILED");
}
